import React from "react";
import { createRoot } from "react-dom/client";
import { ErrorBoundary } from "@draftkings/sb-resp-block-lib";
import { ChildBlock, } from "../../../contracts";
import { dict as defaultDict, config as defaultConfig, PrePacksState, Condition, PrePacksParser, TogglePrePacksStatusRetriever, getModeId, ScoreboardsRetriever, PrePacksRetriever } from "../../../businessLayer";
import * as localizationLib from "@draftkings/sb-localization-lib";
import { disposer, getDefaultValue } from "@draftkings/sportsbook-common-utils";
import { LeagueNavRequestResolver, PrePacksQueryRequestResponseResolver } from "@draftkings/dk-data-layer";
import { getWebHeaders } from "../helpers";
import { GLOBAL_CLIENT_FETCH_TIMEOUT } from "../../../components/helpers/constants";
let mocks;
if (process.env.NODE_ENV === "development") {
    mocks = window["mocks"];
}
if (process.env.NODE_ENV === "test") {
    mocks = require("../../../mocks/all");
}
export class BasePrePacksRoot extends ChildBlock {
    constructor(domId, options) {
        var _a, _b;
        super(domId, options);
        this.activateDataProvider = () => {
            super.activateDataProvider();
            this.isActive = true;
        };
        this.activate = () => {
            super.beforeActivate();
            const domRef = document.getElementById(this.domId);
            if (!domRef) {
                console.warn(`DOM element with id "${this.domId}" couldn't be located on the page.`);
            }
            if (domRef) {
                this.root = createRoot(domRef);
                this.root.render(React.createElement(ErrorBoundary, null, this.getComponent()));
                this.isActive = true;
            }
            super.afterActivate();
            return this;
        };
        this.processSelections = (data) => {
            if (this.isActive) {
                this.dataProvider.processSelections(data);
            }
        };
        this.setParams = (params) => {
            if (!this.isActive) {
                this.dataProvider.setParams(params);
            }
        };
        this.setOddsStyle = (oddsStyle) => {
            this.dataProvider.setOddsStyle(oddsStyle);
        };
        this.updateScoreboards = (scoreboardData) => {
            if (this.isActive && !this.buildScoreboardInternaly) {
                this.scoreboardsRetriever.updateData(scoreboardData);
            }
        };
        const inGameState = options.inGameState || "preLive";
        const config = Object.assign(Object.assign({}, defaultConfig), (options.cms || {}));
        const dict = Object.assign(Object.assign({}, defaultDict), (options.dict || {}));
        const page = `${options.mode.type}Page`;
        const condition = new Condition(PrePacksRetriever.ConditionKey);
        const { leagueNavRequestResolver } = this.createConnectionAndResolver(options);
        const prePacksRequestResolver = new PrePacksQueryRequestResponseResolver({
            baseUrl: options.prePacksBff || "",
            requestTimeout: getDefaultValue(options.requestTimeout, GLOBAL_CLIENT_FETCH_TIMEOUT),
            consumerMetadata: getWebHeaders(page, options.consumerVersion),
        });
        this.scoreboardsRetriever = new ScoreboardsRetriever({
            leagueNavRequestResolver,
            SportsbookLeague: (_a = options.dkDataLayer) === null || _a === void 0 ? void 0 : _a.SportsbookLeague,
            condition,
        });
        this.buildScoreboardInternaly = getDefaultValue(options.buildScoreboardInternaly, false);
        const locale = this.getOption(options, "locale", "en");
        const getEventPageNavLink = this.getOption(options, "getNavLink", (props) => React.createElement(React.Fragment, null, props.children));
        const retriever = new PrePacksRetriever({
            locale,
            eventId: getModeId(options.mode, "Event"),
            leagueId: getModeId(options.mode, "League"),
            condition,
            inGameState,
            throttleTimeout: options.throttleTimeout,
            page,
            prePacksRequestResolver,
            SportsbookPrePacks: (_b = options.dkDataLayer) === null || _b === void 0 ? void 0 : _b.SportsbookPrePacks,
        });
        // Hardcoding "en" as spanish "es" formats the points with comma instead of dot
        localizationLib.init({
            localeSettings: {
                currentLocale: "en",
                localeCodeOverride: "en",
            },
        });
        const parser = new PrePacksParser({
            mode: options.mode,
            dict,
            oddsStyle: options.oddsStyle,
            localizationLib,
            consumerCommands: options.consumerCommands,
            prePacksRetriever: retriever,
            scoreboardsRetriever: this.scoreboardsRetriever,
            togglePrePacksStatusesRetriever: new TogglePrePacksStatusRetriever(),
            isMobile: this.getOption(options, "isMobile", () => false),
            onMobileToggleSelection: this.getOption(options, "onMobileToggleSelection", disposer),
            isSwooshTeamLogosEnabledForPrePacks: getDefaultValue(options.isSwooshTeamLogosEnabledForPrePacks, false),
            swooshBaseUrl: getDefaultValue(options.swooshBaseUrl, ""),
            buildScoreboardInternaly: this.buildScoreboardInternaly,
            logosMap: options.mode.params.teamsLogos,
            staticS3Host: options.mode.params.staticS3Host,
        });
        this.dataProvider = new PrePacksState({
            mode: options.mode,
            dict,
            config,
            inGameState,
            parser,
            condition,
            consumerCommands: options.consumerCommands,
            getNavLink: this.getOption(options, "getNavLink", () => React.createElement(React.Fragment, null)),
            getLiveScoreboard: this.getOption(options, "getLiveScoreboard", () => (React.createElement(React.Fragment, null))),
            getEventPageNavLink,
            getDateCell: this.getOption(options, "getDateCell", () => React.createElement(React.Fragment, null)),
            getTeamLogo: this.getOption(options, "getTeamLogo", () => React.createElement(React.Fragment, null)),
            locale,
        });
        this.props = {
            dataProvider: this.dataProvider,
            buildScoreboardInternaly: this.buildScoreboardInternaly,
        };
        this.component = this.getComponent();
    }
    createConnectionAndResolver(options) {
        let leagueNavRequestResolver;
        if (process.env.NODE_ENV !== "production") {
            leagueNavRequestResolver = new LeagueNavRequestResolver({
                baseUrl: options.navigationBff || "",
                requestTimeout: getDefaultValue(options.requestTimeout, GLOBAL_CLIENT_FETCH_TIMEOUT),
            });
            leagueNavRequestResolver =
                new mocks.PrePacks.LeagueNavRequestResolverMock({
                    baseUrl: "https://sportsbook-nash.draftkingstest.com/api/sportscontent/navigation/dk-sandbox30/",
                    requestTimeout: getDefaultValue(options.requestTimeout, GLOBAL_CLIENT_FETCH_TIMEOUT),
                });
        }
        if (process.env.NODE_ENV === "production") {
            leagueNavRequestResolver = new LeagueNavRequestResolver({
                baseUrl: options.navigationBff || "",
                requestTimeout: getDefaultValue(options.requestTimeout, GLOBAL_CLIENT_FETCH_TIMEOUT),
            });
        }
        return { leagueNavRequestResolver };
    }
    deactivate() {
        var _a;
        super.beforeDeactivate();
        const domRef = document.getElementById(this.domId);
        if (domRef) {
            (_a = this.root) === null || _a === void 0 ? void 0 : _a.unmount();
            this.isActive = false;
        }
        super.afterDeactivate();
        return this;
    }
    getOption(options, option, defaultValue) {
        return options[option] || defaultValue;
    }
}
//# sourceMappingURL=index.js.map